import { ref } from 'vue'

export const useImageZoom = () => {
  // Zoom state
  const zoomLevel = ref(1)
  const panX = ref(0)
  const panY = ref(0)
  const isDragging = ref(false)
  const lastTouchDistance = ref(0)
  const lastTouchTime = ref(0)
  const touchStartX = ref(0)
  const touchStartY = ref(0)

  /**
   * Reset zoom and pan to initial state
   */
  const resetZoom = () => {
    zoomLevel.value = 1
    panX.value = 0
    panY.value = 0
    isDragging.value = false
  }

  /**
   * Calculate distance between two touch points
   */
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX
    const dy = touches[0].clientY - touches[1].clientY
    return Math.sqrt(dx * dx + dy * dy)
  }

  /**
   * Handle touch start for pinch-to-zoom and pan
   */
  const handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      // Pinch gesture start
      lastTouchDistance.value = getTouchDistance(event.touches)
      isDragging.value = true
    } else if (event.touches.length === 1 && zoomLevel.value > 1) {
      // Single touch for panning when zoomed
      isDragging.value = true
      const touch = event.touches[0]
      touchStartX.value = touch.clientX - panX.value
      touchStartY.value = touch.clientY - panY.value
    }
  }

  /**
   * Handle touch move for pinch-to-zoom and pan
   */
  const handleTouchMove = (event) => {
    event.preventDefault()

    if (event.touches.length === 2 && lastTouchDistance.value) {
      // Pinch zoom
      const currentDistance = getTouchDistance(event.touches)
      const scale = currentDistance / lastTouchDistance.value
      const newZoom = Math.max(1, Math.min(3, zoomLevel.value * scale))

      zoomLevel.value = newZoom
      lastTouchDistance.value = currentDistance
    } else if (event.touches.length === 1 && zoomLevel.value > 1 && isDragging.value) {
      // Pan when zoomed
      const touch = event.touches[0]
      panX.value = touch.clientX - touchStartX.value
      panY.value = touch.clientY - touchStartY.value
    }
  }

  /**
   * Handle touch end
   */
  const handleTouchEnd = () => {
    isDragging.value = false
    lastTouchDistance.value = 0
  }

  /**
   * Handle double-click/tap for zoom toggle
   */
  const handleDoubleClick = (event) => {
    const now = Date.now()
    if (now - lastTouchTime.value < 300) {
      // Double tap detected
      if (zoomLevel.value === 1) {
        // Zoom in to 3x
        zoomLevel.value = 3
        // Center on tap point
        const rect = event.currentTarget.getBoundingClientRect()
        const centerX = (event.clientX - rect.left - rect.width / 2) / 2
        const centerY = (event.clientY - rect.top - rect.height / 2) / 2
        panX.value = -centerX
        panY.value = -centerY
      } else {
        // Reset zoom
        resetZoom()
      }
    }
    lastTouchTime.value = now
  }

  /**
   * Get transform style object for zoom and pan
   */
  const getTransformStyle = () => ({
    transform: `scale(${zoomLevel.value}) translate(${panX.value}px, ${panY.value}px)`,
    transition: isDragging.value ? 'none' : 'transform 0.3s ease',
  })

  return {
    // State
    zoomLevel,
    panX,
    panY,
    isDragging,

    // Methods
    resetZoom,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleDoubleClick,
    getTransformStyle,
  }
}
